import { computed, ref } from 'vue';
import useMainApiRequest from '@composables/useMainApiRequest';
import { registerCacheInvalidation } from '@composables/useCache';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { has, set, get, unset, union } from 'lodash-es';
import { MP_LINK_GLAYER_IMPORT_SELECT, MP_MODEL_STA_DATASET, MP_MODEL_STA_VIEWER } from '@keys/index';
import useModels from '@composables/useModels';
import { invalidateCalibrations } from '@composables/useModelCalibrations';

export type TmDataset = {
  id: number;
  name: string;
  description: string;
  type: 'calibration' | 'validation';
  createdAt: TmDate;
  flows: { edgeId: number; flow: number }[];
  dateFilters?: (Partial<TmDateFilters> & { dateFrom: TmDate; dateTo: TmDate }) | null;
};
export type GlayerResultData = null | Partial<{
  flows: TmDataset['flows'];
  dateFrom: TmDate;
  dateTo: TmDate;
  dateFilters: Partial<TmDateFilters>;
}>;

const cachedDatasets = ref<Record<TmDataset['id'], TmDataset>>({});
const isOverviewCached = ref(false);
const cachedGlayerResult = ref<GlayerResultData>(null);
const cachedAbsentEdges = ref<Record<TmTrafficModel['id'], number[]>>({});

export default function useModelDatasets() {
  const router = useRouter();
  const store = useStore();
  const { t } = useI18n();
  const { makeRequest } = useMainApiRequest();
  const { fetchModels } = useModels();

  const trafficModelId = computed(() => store.state.scenarios.model.id);

  const fetchDatasets = () => {
    if (isCacheAvailable()) return;

    return makeRequest({
      url: `datasets/traffic-model/${trafficModelId.value}`,
      method: 'get',
      message: {
        error: {
          404: { summary: t('models.no datasets found'), severity: 'info' },
          default: t('models.error while fetching datasets'),
        },
      },
      onSuccess: (result: { datasets: TmDataset[] }) => {
        result.datasets.forEach((dataset) => {
          set(cachedDatasets.value, dataset.id, dataset);
        });
        isOverviewCached.value = true;
      },
    });
  };

  const fetchDataset = (datasetId: TmDataset['id']) => {
    if (isCacheAvailable(datasetId)) return; // return only if full dataset (with flows) is cached

    return makeRequest({
      url: `datasets/${datasetId}`,
      method: 'get',
      message: {
        error: t('models.error while fetching dataset'),
      },
      onSuccess: (result: { dataset: TmDataset }) => {
        set(cachedDatasets.value, result.dataset.id, result.dataset);
      },
    });
  };

  const fetchGlayerData = async (
    selectedFeaturesIds: TmFeatureId[],
    dateFrom: TmDate,
    dateTo: TmDate,
    dateFilters: TmDateFilters,
  ) => {
    return makeRequest({
      url: 'glayer/traffic',
      method: 'get',
      params: {
        edgeIds: selectedFeaturesIds.join(),
        dateFrom,
        dateTo,
        ...(dateFilters.hours?.length && { hours: dateFilters.hours.join() }),
        ...(dateFilters.days?.length && { days: dateFilters.days.join() }),
        ...(dateFilters.months?.length && { months: dateFilters.months.join() }),
      },
      message: {
        error: t('models.dataset import error'),
      },
      onSuccess: (result: { glayerTraffic: GlayerResultData }) => {
        cachedGlayerResult.value = result.glayerTraffic;
        store.dispatch('map/setMapMode', { mode: MP_MODEL_STA_DATASET }); // go back to dataset map mode
      },
    });
  };

  const createDataset = (dataset: Omit<TmDataset, 'id'>) => {
    return makeRequest({
      url: `datasets`,
      method: 'post',
      data: {
        name: dataset.name,
        description: dataset.description,
        type: dataset.type,
        trafficModelId: trafficModelId.value,
        flows: dataset.flows,
        dateFilters: dataset.dateFilters,
      },
      message: {
        error: {
          severity: 'error',
          summary: t('models.error while creating dataset'),
        },
        success: {
          severity: 'success',
          summary: t('models.dataset created successfully'),
          detail: t('models.result can be displayed on the map'),
        },
      },
      timeout: 10 * 1000, // 10 sec
      onSuccess: (result: { dataset: TmDataset }) => {
        set(cachedDatasets.value, result.dataset.id, result.dataset);
        router.push({ name: 'models.datasets' });
      },
      onFailure(result: { data: { absentEdges?: number[] }; [key: string]: unknown }) {
        if (!result.data?.absentEdges) return;
        set(cachedAbsentEdges.value, trafficModelId.value, union(absentEdges.value, result.data.absentEdges));
      },
    });
  };

  const deleteDataset = (datasetId: TmDataset['id']) => {
    return makeRequest({
      url: `datasets/${datasetId}`,
      method: 'delete',
      message: {
        success: t('models.dataset deleted successfully'),
        error: t('models.error while deleting dataset'),
      },
      onSuccess: () => {
        invalidateDatasets(datasetId);
        invalidateCalibrations(); // some calibrations might contain reference to this dataset
        previewDataset(); // clear previewed dataset
        router.push({ name: 'models.datasets' });
      },
    });
  };

  const updateDataset = (datasetId: TmDataset['id'], dataset: Partial<TmDataset>) => {
    return makeRequest({
      url: `datasets/${datasetId}`,
      method: 'patch',
      data: {
        name: dataset.name,
        description: dataset.description,
        type: dataset.type,
      },
      message: {
        success: t('models.dataset updated successfully'),
        error: t('models.error while updating dataset'),
      },
      onSuccess: (result: { dataset: TmDataset }) => {
        const originalDataset = get(cachedDatasets.value, datasetId, {});
        const { name, description, type } = result.dataset; // only update relevant data from response - preserve cached flows
        set(cachedDatasets.value, datasetId, { ...originalDataset, name, description, type });
        router.push({ name: 'models.datasets' });
      },
    });
  };

  const previewDataset = (id?: TmDataset['id']) => store.dispatch('map/previewDataset', id);

  const handleDatasetEnter = async () => {
    await fetchModels(); // ensure traffic models are already fetched
    store.dispatch('map/setMapMode', { mode: MP_MODEL_STA_DATASET });
  };

  const handleDatasetExit = () => {
    cachedGlayerResult.value = null; // clear any old glayer result
    previewDataset(); // clear previewed dataset
    const datasetRouteNames = ['models.datasets.create', 'models.datasets.edit', 'models.datasets'];
    if (datasetRouteNames.includes(String(router.currentRoute.value.name))) return;
    if (![MP_MODEL_STA_DATASET, MP_LINK_GLAYER_IMPORT_SELECT].includes(store.state.map.mapMode)) return;
    // resume model map mode (only if user is not trying to go to another map mode directly, or redirecting to dataset routes)
    store.dispatch('map/setMapMode', { mode: MP_MODEL_STA_VIEWER });
  };

  const initiateGlayerImport = () => {
    cachedGlayerResult.value = {}; // empty object indicates import initiated
    store.dispatch('map/setMapMode', { mode: MP_LINK_GLAYER_IMPORT_SELECT }); // set map mode first so to enable map mode specific widget
    store.dispatch('map/showWidget', { name: 'glayerDatasetImport' });
  };

  const calculateGehStatistics = (m: number, c: number) => Math.sqrt((2 * Math.pow(m - c, 2)) / (m + c));

  const absentEdges = computed(() => cachedAbsentEdges.value[trafficModelId.value] || []);

  return {
    datasets: computed(() => Object.values(cachedDatasets.value)),
    previewedDatasetId: computed<TmDataset['id'] | undefined>(() => store.getters['map/getPreviewedDatasetId']),
    glayerResult: computed(() => cachedGlayerResult.value),
    isGlayerSelectionActive: computed(() => !!cachedGlayerResult.value),
    isGlayerSelectionFinished: computed(() => !!cachedGlayerResult.value?.flows),
    fetchDatasets,
    fetchDataset,
    fetchGlayerData,
    createDataset,
    deleteDataset,
    updateDataset,
    getDatasetById: (id: TmDataset['id']) => get(cachedDatasets.value, id),
    previewDataset,
    handleDatasetEnter,
    handleDatasetExit,
    invalidateDatasets,
    initiateGlayerImport,
    calculateGehStatistics,
    absentEdges,
    isEdgeAbsent: (id: number) => absentEdges.value.includes(id),
  };
}

const isCacheAvailable = (id?: TmDataset['id']) => {
  if (id) return has(cachedDatasets.value, `${id}.flows`);
  return isOverviewCached.value && !!Object.keys(cachedDatasets.value).length;
};

export const invalidateDatasets = (id?: TmDataset['id']) => {
  if (id) return unset(cachedDatasets.value, id);
  cachedDatasets.value = {};
  isOverviewCached.value = false;
  cachedGlayerResult.value = null;
  cachedAbsentEdges.value = {};
};

registerCacheInvalidation('datasets', invalidateDatasets);
